import { Badge, Button, Card, Center, HStack, Icon, IconButton, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useEffect } from 'react';
import { LocalAttendanceStatement } from '../../../../../types';
import { MdCompareArrows, MdEdit, MdFileDownload, MdFileUpload, MdInfoOutline, MdPrint } from 'react-icons/md';
import { TextValueField } from '../../../../common/TextValueField';
import PopoverHelper from '../../../../common/PopoverHelper';
import { CheckIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { UploadIcon } from '../statementListComponents/StatementTableItem';
import { getDocumentUploadStatus } from '../../../../../utils/DocumentUtils';
import { StatementDiffModal } from './StatementDiffModal';
import { useSuIntl } from '../../../../../hooks/useSuIntl';
import { FiDownload } from 'react-icons/fi';
import { useDownloadDocument } from '../../export/useDownloadDocument';
import { UserDetailsCard } from '../../shared/UserDetailsCard';
import { UserCustomFieldValue } from '../../../../../types/customFields';

type EditorSideMenuProps = {
    data: LocalAttendanceStatement;
    isEditing: boolean;
    onToggleEdit: () => void;
    onUpload: () => void;
    isUploading: boolean;
    onDelete: () => void;
    isDeleting: boolean;
    onDeleteLocalChanges: () => void;
    onCustomFieldsLoaded?: (fields: UserCustomFieldValue[]) => void;
};

export const EditorSideMenu: React.FC<EditorSideMenuProps> = ({
    data,
    isEditing,
    onToggleEdit,
    isUploading,
    onUpload,
    onDeleteLocalChanges,
    onDelete,
    isDeleting,
    onCustomFieldsLoaded,
}) => {
    const { t, formatDate } = useSuIntl();

    const uploadStatus = getDocumentUploadStatus({ externalId: data.externalId, localId: data.id });
    const useDownload = useDownloadDocument();
    const diffDisclosure = useDisclosure();

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                useDownload.handlePrint(data);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <VStack align="stretch" gap={6}>
            <Card p={4} boxShadow={'md'} borderRadius={'xl'} minW={270}>
                <HStack justify={'space-between'} align={'center'} mb={4}>
                    <Text fontSize={'lg'} fontWeight={'500'}>
                        {t('editor.options')}
                    </Text>
                    <UploadIcon size={25} status={uploadStatus} />
                </HStack>
                <HStack mb={6} align={'center'} justify={'space-between'}>
                    <TextValueField smallVariant label={t('field.createdAt')} value={formatDate(data.createdAt)} icon={<EditIcon />} />
                    <TextValueField smallVariant label={t('field.updatedAt')} value={formatDate(data.lastChangeAt)} icon={<MdEdit />} />
                </HStack>
                {uploadStatus === 'changed' && (
                    <HStack justify={'center'} pb={4}>
                        <Badge borderRadius={'md'}>{t('doc.uploadStatus.changed.title')}</Badge>
                    </HStack>
                )}

                <VStack>
                    <Button
                        variant={isEditing ? 'outline' : 'solid'}
                        w={'100%'}
                        onClick={onToggleEdit}
                        leftIcon={isEditing ? <CheckIcon /> : <MdEdit />}
                        colorScheme={isEditing ? 'primary' : 'green'}
                    >
                        {t(isEditing ? 'editor.done' : 'edit')}
                    </Button>
                    <HStack justify={'space-between'} w={'100%'}>
                        <Button onClick={onUpload} isLoading={isUploading} w={'100%'} leftIcon={<MdFileUpload />} colorScheme="green">
                            {t(data.externalId ? 'doc.uploadChanges.title' : 'doc.upload.title')}
                        </Button>
                        <PopoverHelper
                            triggerComponent={
                                <Center>
                                    <Icon color={'gray.500'} as={MdInfoOutline} boxSize={'6'} mx={'auto'} my={'auto'} />
                                </Center>
                            }
                        >
                            <VStack p={4} align={'start'}>
                                <Text fontSize={'sm'}> {t(data.externalId ? 'doc.uploadChanges.text' : 'doc.upload.text')}</Text>
                            </VStack>
                        </PopoverHelper>
                    </HStack>
                    {uploadStatus === 'changed' && (
                        <>
                            <HStack justify={'space-between'} w={'100%'}>
                                <Button leftIcon={<MdCompareArrows />} onClick={diffDisclosure.onOpen} colorScheme="yellow" width={'100%'}>
                                    {t('doc.compareChanges')}
                                </Button>
                                <PopoverHelper
                                    triggerComponent={
                                        <Center>
                                            <Icon color={'gray.500'} as={MdInfoOutline} boxSize={'6'} mx={'auto'} my={'auto'} />
                                        </Center>
                                    }
                                >
                                    <VStack p={4} align={'start'}>
                                        <Text fontSize={'sm'}>{t('doc.compareChanges.text')}</Text>
                                    </VStack>
                                </PopoverHelper>
                            </HStack>
                            <Button leftIcon={<MdFileDownload />} onClick={onDeleteLocalChanges} colorScheme="yellow" width={'100%'}>
                                {t('doc.discardChanges')}
                            </Button>
                        </>
                    )}
                    <HStack width={'100%'}>
                        <IconButton
                            size={'sm'}
                            onClick={() => useDownload.handlePrint(data)}
                            colorScheme="gray"
                            isLoading={useDownload.isLoading}
                            aria-label="print"
                            icon={<MdPrint size={26} />}
                            flex={1}
                        />
                        <IconButton
                            size={'sm'}
                            isLoading={useDownload.isLoading}
                            isDisabled={useDownload.isCompanyLoading}
                            onClick={() => useDownload.handleDownload(data)}
                            colorScheme="gray"
                            aria-label="download pdf"
                            icon={<FiDownload size={26} />}
                            flex={1}
                        />
                    </HStack>
                </VStack>

                <Button
                    mt={4}
                    onClick={onDelete}
                    isLoading={isDeleting}
                    variant={'ghost'}
                    w={'100%'}
                    leftIcon={<DeleteIcon />}
                    colorScheme="red"
                >
                    {t('delete')}
                </Button>
            </Card>
            {uploadStatus === 'changed' && !!data.externalId && (
                <StatementDiffModal
                    isOpen={diffDisclosure.isOpen}
                    onClose={diffDisclosure.onClose}
                    localData={data}
                    externalId={data.externalId}
                    onUseLocalData={() => {
                        onUpload();
                        diffDisclosure.onClose();
                    }}
                    onUseRemoteData={() => {
                        onDeleteLocalChanges();
                        diffDisclosure.onClose();
                    }}
                />
            )}
            <UserDetailsCard userId={data.userId} userName={data.name} onCustomFieldsLoaded={onCustomFieldsLoaded} />
        </VStack>
    );
};
